// PackageDetails.jsx
import { useState, useEffect } from "react";
import { Box, CircularProgress, Divider } from "@mui/material";
import FieldsTable from "@/Components/Tables/FieldsTable";
import { SectionHeader } from "@/Components/Header/SectionsHeader";
import { LuX } from "@/Components/Icons/Icons";
import { OutLinedButton } from "@/Components/Buttons/OutLinedButton";
import { BlackButton } from "@/Components/Buttons/FiledButton";
import {
  useCreatePackageDetailsMutation,
  useUpdatePackageDetailsMutation,
  useDeletePackageDetailsMutation,
  useGetPackageDetailsByMasterIdQuery,
} from "@/redux/ExportPage/CreateNewJob/Container/ContainerPackageSlice";
import { useGetQuantityQuery } from "@/redux/Masters/Masters";
import SuccessDialog from "@/Components/Dialog/SuccessDialog";
import { BiSolidError } from "react-icons/bi";
import { LuCheck } from "@/Components/Icons/Icons";
import { useUser } from "@/Context/UserContext";

export default function PackageDetails({ onClose, packingdetails }) {
  const [rows, setRows] = useState([]);
  const [createPackageDetails] = useCreatePackageDetailsMutation();
  const [updatePackageDetails] = useUpdatePackageDetailsMutation();
  const [deletePackageDetails] = useDeletePackageDetailsMutation();
  const { data: quantityOptions = [] } = useGetQuantityQuery();
  const [loading, setLoading] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const { exporterMasterId } = useUser();
  const { data: packageData, isFetching } =
    useGetPackageDetailsByMasterIdQuery(exporterMasterId);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  // 🔹 Save button disabled if any row missing required fields
  // const isSaveDisabled = rows.some((row) => {
  //   const from = row.packingDetailsFrom?.value?.[0];
  //   const to = row.packingDetailsTo?.value?.[0];
  //   const typeId = row.packingDetailsType?.value?.[0];
  //   return !from || !to || !typeId;
  // });

  // Update save button enable/disable based on row validation
  useEffect(() => {
    const disabled = !rows.some(
      (row) =>
        row.editable && // enable if any row is editable
        row.packingDetailsFrom?.value?.[0] &&
        row.packingDetailsTo?.value?.[0] &&
        row.packingDetailsType?.value?.[0] &&
        !row.error
    );
    setIsSaveDisabled(disabled);
  }, [rows]);

  // 🔥 Map API → Table
  const mapApiToTable = (apiData) => {
    return apiData.map((item, idx) => ({
      id: item.packingDetailsId || idx + 1,
      editable: false,
      srno: { type: ["display"], value: [String(idx + 1)] },
      packingDetailsFrom: {
        type: ["number"],
        value: [item.packingDetailsFrom || ""],
        disabled: [true],
      },
      packingDetailsTo: {
        type: ["number"],
        value: [item.packingDetailsTo || ""],
        disabled: [true],
      },
      packingDetailsEntryDate: {
        type: ["DateSelector"],
        value: [item.packingDetailsEntryDate || ""],
        disabled: [true],
      },
      packagecount: {
        type: ["number"],
        value: [
          Number(item.packingDetailsFrom || 0) *
            Number(item.packingDetailsTo || 0),
        ],
        disabled: [true],
      },
      packingDetailsType: {
        type: ["dropdown"],
        value: [item.packingDetailsType || ""],
        disabled: [true],
        options: quantityOptions.map((q) => ({ value: q.id, label: q.name })),
      },
      action: { type: ["action"] },
    }));
  };

  // 🟡 Load API Data into Table
  useEffect(() => {
    if (!isFetching && packageData?.length) {
      setRows(mapApiToTable(packageData));
    }
  }, [packageData, quantityOptions, isFetching]);

  // 🔑 Table Columns
  const columnFields = [
    { key: "srno", label: "Sr No", width: "70px" },
    { key: "packingDetailsFrom", label: "From", width: "120px" },
    { key: "packingDetailsTo", label: "To", width: "120px" },
    { key: "packagecount", label: "Package Count", width: "180px" },
    { key: "packingDetailsType", label: "Package Type", width: "200px" },
    { key: "action", label: "Action", width: "120px" },
  ];

  // 🟡 Handle Value Change
  const handleChange = ({ rowId, key, value }) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          const updatedRow = {
            ...row,
            [key]: { ...row[key], value: [value] },
            error: "", // clear previous error
          };

          // Calculate for UI only
          const fromNum =
            key === "packingDetailsFrom"
              ? Number(value)
              : Number(row.packingDetailsFrom.value[0] || 0);
          const toNum =
            key === "packingDetailsTo"
              ? Number(value)
              : Number(row.packingDetailsTo.value[0] || 0);

          // Validation
          if (fromNum > toNum) {
            updatedRow.error = "From value cannot be greater than To value";
          }

          // UI-only package count
          updatedRow.packagecount = {
            ...updatedRow.packagecount,
            value: [fromNum * toNum],
          };

          return updatedRow;
        }
        return row;
      })
    );
  };

  // ✏️ Enable Edit Mode
  const handleEdit = (row) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id
          ? {
              ...r,
              editable: true,
              packingDetailsFrom: {
                ...r.packingDetailsFrom,
                disabled: [false],
              },
              packingDetailsTo: { ...r.packingDetailsTo, disabled: [false] },
              packingDetailsEntryDate: {
                ...r.packingDetailsEntryDate,
                disabled: [false],
              },
              packingDetailsType: {
                ...r.packingDetailsType,
                disabled: [false],
              },
            }
          : r
      )
    );
  };

  // 🗑️ Delete Row
  const handleDelete = async (row) => {
    try {
      if (row.id && !String(row.id).startsWith("temp-")) {
        await deletePackageDetails({ packingDetailsId: row.id }).unwrap();
      }
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (err) {
      console.error("❌ Delete failed:", err);
    }
  };

  // ➕ Add Row
  const handleAddRow = () => {
    const newId = `temp-${rows.length + 1}`;
    const newRow = {
      id: newId,
      editable: true,
      srno: { type: ["display"], value: [String(rows.length + 1)] },
      packingDetailsFrom: { type: ["number"], value: [""], disabled: [false] },
      packingDetailsTo: { type: ["number"], value: [""], disabled: [false] },
      packingDetailsEntryDate: {
        type: ["DateSelector"],
        value: [""],
        disabled: [false],
      },
      packagecount: { type: ["number"], value: [0], disabled: [true] },
      packingDetailsType: {
        type: ["dropdown"],
        value: [""],
        disabled: [false],
        options: quantityOptions.map((q) => ({ value: q.id, label: q.name })),
      },
      action: { type: ["action"] },
    };
    setRows((prev) => [...prev, newRow]);
  };

  // 💾 Save (Create + Update)
  const handleSave = async () => {
    setLoading(true);
    try {
      const updatedRows = [...rows];

      console.log("updatedRows to send to API", updatedRows);
      for (let i = 0; i < updatedRows.length; i++) {
        const row = updatedRows[i];
        console.log("updatedRows to send to API", row);

        if (!row.editable) continue;

        const from = row.packingDetailsFrom?.value?.[0];
        const to = row.packingDetailsTo?.value?.[0];
        const typeId = row.packingDetailsType?.value?.[0].id;

        if (!from || !to || !typeId) continue;

        const payload = {
          packingDetailsFrom: from,
          packingDetailsTo: to,
          packingDetailsType: typeId,
        };
        console.log("payload to send to API", payload);

        if (row.id && !String(row.id).startsWith("temp-")) {
          await updatePackageDetails({
            exporterMasterId: exporterMasterId,
            packingDetailsId: row.id,
            packingDetails: payload,
          }).unwrap();
        } else {
          const response = await createPackageDetails({
            exporterMasterId: exporterMasterId,
            packingDetails: payload,
          }).unwrap();
          row.id = response.id;
        }

        // Lock row but keep packagecount unchanged
        updatedRows[i] = {
          ...row,
          editable: false,
          packingDetailsFrom: { ...row.packingDetailsFrom, disabled: [true] },
          packingDetailsTo: { ...row.packingDetailsTo, disabled: [true] },
          packingDetailsEntryDate: {
            ...row.packingDetailsEntryDate,
            disabled: [true],
          },
          packingDetailsType: { ...row.packingDetailsType, disabled: [true] },
          packagecount: row.packagecount,
        };
      }

      setRows(updatedRows);

      setSuccessDialogConfig({
        label: "Success",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            Data Save Successfully
          </strong>
        ),
        buttonText: "OK",
        IconComponent: LuCheck,
      });
      setShowSuccessDialog(true);
    } catch (error) {
      console.error("❌ Save failed:", error);
      setSuccessDialogConfig({
        label: "Failure",
        title: "",
        subtitle: (
          <strong style={{ fontSize: "1rem", color: "var(--brand-900)" }}>
            {error?.data?.message || "Something went wrong"}
          </strong>
        ),
        buttonText: "OK",
        status: "error",
        icon: BiSolidError,
      });
      setShowSuccessDialog(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--gray-0)",
        width: "100%",
        maxWidth: "100vw",
        height: 400,
        mb: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingRight: 2,
          m: 0.5,
        }}
      >
        <SectionHeader title={"Packing Details"} />
        <LuX size={20} style={{ cursor: "pointer" }} onClick={onClose} />
      </Box>
      <Divider />

      {/* Add Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", pr: 2, m: 1 }}>
        <BlackButton
          variant="contained"
          color="primary"
          onClick={handleAddRow}
          label="Add"
          sx={{ border: "none", width: 100, mb: 0 }}
        />
      </Box>

      {/* Table */}
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        {isFetching && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              width: "100%",
              gap: 1,
            }}
          >
            <CircularProgress size={40} sx={{ color: "var(--brand-600)" }} />
          </Box>
        )}
        {!isFetching && (
          <FieldsTable
            rowData={rows}
            columnFields={columnFields}
            onChange={handleChange}
            onEdit={handleEdit}
            onDelete={handleDelete}
            colkey="key"
          />
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
          justifyContent: "flex-end",
          borderTop: "1px solid var(--gray-400)",
        }}
      >
        <OutLinedButton
          variant="contained"
          color="secondary"
          onClick={onClose}
          label="Cancel"
          sx={{ width: 100, mt: 3 }}
        />
        <BlackButton
          variant="contained"
          color="secondary"
          onClick={handleSave}
          label={loading ? "Saving..." : "Save"}
          disabled={loading || isSaveDisabled}
          sx={{ mr: 5, width: 100, mt: 3 }}
        />
      </Box>

      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        {...successDialogConfig}
      />
    </Box>
  );
}
